class NumberParser {
  private _group: RegExp;
  private _decimal: RegExp;
  private _numeral: RegExp;
  private _index: (d: string) => number | undefined;
  private _currency: RegExp;

  constructor(locale: string, currency: string) {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "standard",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 1
    }).formatToParts(12345.6);
    const numerals = Array.from(
      new Intl.NumberFormat(locale, { useGrouping: false }).format(9876543210)
    ).reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));

    const groupValue = parts.find((d) => d.type === "group")?.value;
    const decimalValue = parts.find((d) => d.type === "decimal")?.value;
    const currencyValue = parts.find((d) => d.type === "currency")?.value;

    // Escape special regex characters for group and decimal separators
    const escapeRegex = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    // Check if the group separator is any type of space
    const isSpaceSeparator =
      groupValue && /[\s\u00A0\u2009\u202F]/.test(groupValue);

    // Check if the group separator is any type of apostrophe/quote
    const isApostropheSeparator =
      groupValue && /[''\u2019\u0027]/.test(groupValue);

    // If locale uses space as separator, match all types of spaces to handle user input variations
    // (users might type regular space when locale expects non-breaking space)
    // Similarly, if locale uses apostrophe, match all apostrophe variants (straight and curly)
    this._group = groupValue
      ? isSpaceSeparator
        ? new RegExp(`[\\s\\u00A0\\u2009\\u202F]`, "g")
        : isApostropheSeparator
        ? new RegExp(`[''\u2019\u0027]`, "g")
        : new RegExp(escapeRegex(groupValue), "g")
      : new RegExp(`[,.'\\s\\u00A0\\u2009\\u202F]`, "g");
    this._decimal = decimalValue
      ? new RegExp(escapeRegex(decimalValue))
      : new RegExp(`[.]`);
    this._numeral = new RegExp(`[${numerals.join("")}]`, "g");
    this._index = (d) => index.get(d);
    this._currency = currencyValue
      ? new RegExp(escapeRegex(currencyValue), "g")
      : new RegExp(`[^\\d\\s.,]`, "g");
  }

  parse(string: string): number {
    return (string = string
      .trim()
      .replace(this._group, "")
      .replace(this._currency, "")
      .replace(this._decimal, ".")
      .replace(this._numeral, (d) => String(this._index(d)))).trim()
      ? +string
      : NaN;
  }
}

export default NumberParser;
